import { createActionGroup, emptyProps, props } from '@ngrx/store';

import { ScheduleActivity, ScheduleCategory, WeeklySchedule } from '@core/models';

/**
 * Weekly Schedule actions.
 *
 * The unit of change is ONE activity or ONE category, never the whole week. That
 * mirrors `docs/backend-spec-weekly-schedule.md` §4 — each modal Save becomes one
 * call — which is why there is no edit buffer, no pending count and no Discard:
 * there is nothing to hold, because a save is a save.
 */
export const SchedulePageActions = createActionGroup({
  source: 'Schedule Page',
  events: {
    Entered: emptyProps(),
    /**
     * Save one activity, removing any it overwrites — one action, because an
     * overwrite that split into a remove and an add would leave the week briefly
     * wrong and, over the network, half-written if the second call failed.
     *
     * `created` says POST or PATCH. The guid on a new activity is the client's
     * placeholder until the server answers with its own; locally it is simply the
     * key the row is stored under.
     */
    'Activities Saved': props<{ personaCode: string; removed: string[]; activity: ScheduleActivity; created: boolean }>(),
    'Activity Removed': props<{ personaCode: string; activityGuid: string }>(),
    /** Add or replace one category, matched on `categoryGuid`. */
    'Category Saved': props<{ personaCode: string; category: ScheduleCategory; created: boolean }>(),
    'Category Removed': props<{ personaCode: string; categoryGuid: string }>(),
  },
});

export const ScheduleApiActions = createActionGroup({
  source: 'Schedule API',
  events: {
    'Load Success': props<{ schedules: WeeklySchedule[] }>(),
    'Load Failure': props<{ error: string }>(),
    /**
     * One person's week, as the server now has it — dispatched after every write.
     *
     * Re-reading beats reconciling: a create comes back with a guid the client
     * did not choose, and threading that through an optimistic row is a class of
     * bug for the sake of one small GET on a document this size.
     */
    'Week Loaded': props<{ schedule: WeeklySchedule }>(),
    /** The portfolio: the persona codes that HAVE a week. Absence is "not yet". */
    'Portfolio Loaded': props<{ personaCodes: string[] }>(),
  },
});
